// src/components/project/ProjectDetail.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";

// Icons
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import StopIcon from "@mui/icons-material/Stop";

// Markdown + LaTeX
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import remarkGfm from "remark-gfm";
import "katex/dist/katex.min.css";

// Syntax highlighting
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { LIGHT_THEME, DARK_THEME } from "../../config/SyntaxThemes";

// Components
import Config from "../../config/Config";
import SweetLoadingSpinner from "../eyeCandy/SweetLoadingSpinner";
import ProjectContributorsList from "./ProjectContributorsList";

/* ============================================================
   Helpers
============================================================ */
const getLanguage = (className) =>
  (className && /language-([\w-]+)/.exec(className)?.[1]?.toLowerCase()) ||
  "python";

const getCodeString = (children) => {
  if (Array.isArray(children)) return children.join("");
  return String(children ?? "");
};

/* ============================================================
   Renderers for markdown (scoped, a11y-friendly)
   - PreBlock: fenced code with copy button
   - InlineCode: inline code styling
   - TableWrap: horizontal scroll container for wide tables
============================================================ */
const PreBlock = ({ children, isDarkMode }) => {
  const codeEl = Array.isArray(children) ? children[0] : children;
  const className = codeEl?.props?.className || "";
  const language = getLanguage(className);
  const code = getCodeString(codeEl?.props?.children).replace(/\n$/, "");

  const [copied, setCopied] = useState(false);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = code;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  }, [code]);

  return (
    <div className="codeblock-wrapper" style={{ position: "relative" }}>
    <button
      type="button"
      className={`copy-btn ${copied ? 'copied' : ''}`}
      onClick={copy}
      aria-label="Copy code"
      title={copied ? "Copied" : "Copy"}
      style={{
        position: "absolute",
        top: 6,
        right: 6,
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "4px 6px",
        fontSize: 11,
        borderRadius: 4,
        border: "1px solid #e2e8f0",
        background: "rgba(255,255,255,0.9)",
        cursor: "pointer",
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
        opacity: 0.4,
        transition: "opacity 150ms ease, background-color 150ms ease",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
      onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.4")}
    >
      <ContentCopyIcon style={{ fontSize: 14 }} />
      <span className="copy-text" style={{ fontSize: 10 }}>{copied ? "Copied" : "Copy"}</span>
    </button>

      <SyntaxHighlighter
        style={DARK_THEME}
        language={language}
        PreTag="div"
        customStyle={{
          paddingTop: 8,
          borderRadius: 10,
          border: "1px solid #e2e8f0",
          overflowX: "auto",
        }}
      >
        {code}
      </SyntaxHighlighter>
    </div>
  );
};

const InlineCode = ({ className, children, ...props }) => (
  <code
    className={className}
    {...props}
    style={{
      background: "var(--code-bg)",
      color: "var(--code-text)",
      borderRadius: 6,
      padding: "0.1rem 0.35rem",
      fontSize: "0.95em",
    }}
  >
    {children}
  </code>
);

/** Wrap tables in a horizontally scrollable container (matches Section 7 CSS) */
const TableWrap = ({ children, ...props }) => (
  <div className="table-scroll">
    <table {...props}>{children}</table>
  </div>
);

/* ============================================================
   Floating Edit Button (bottom-right)
============================================================ */
const FloatingEditButton = ({ to }) => (
  <Link
    to={to}
    aria-label="Edit project"
    style={{
      position: "fixed",
      bottom: 24,
      right: 24,
      zIndex: 1000,
      width: 64,
      height: 64,
      borderRadius: "50%",
      backgroundColor: "#59c6c1",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "0 8px 18px rgba(0,0,0,0.2)",
      textDecoration: "none",
    }}
  >
    <EditIcon style={{ color: "white", fontSize: "2.1rem" }} />
  </Link>
);

/* ============================================================
   Floating Copy Button — copies project data as JSON
============================================================ */
const FloatingCopyButton = ({ project }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    const { title, subtitle, technology, skill, body, owner } = project;
    const payload = { title, subtitle, technology, skill, body, owner };
    const json = JSON.stringify(payload, null, 2);
    try {
      await navigator.clipboard.writeText(json);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = json;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [project]);

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label="Copy project data as JSON"
      style={{
        position: "fixed",
        bottom: 30,
        right: 100,
        zIndex: 1000,
        width: 44,
        height: 44,
        borderRadius: "50%",
        backgroundColor: copied ? "#e2e8f0" : "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        border: "1px solid var(--border)",
        cursor: "pointer",
        transition: "background-color 200ms ease",
      }}
    >
      <ContentCopyIcon style={{ color: copied ? "#59c6c1" : "#333", fontSize: "1.3rem" }} />
    </button>
  );
};

/* ============================================================
   Extract speakable text SEGMENTS from a DOM element.
   Each block-level element (heading, paragraph, list item, etc.)
   becomes its own segment. Each segment is spoken as a separate
   utterance, creating natural pauses between them.
   - Inline code is read naturally within its parent element.
   - Code blocks (PRE) → each line is its own segment.
   - KaTeX annotations and script/style tags are skipped.
============================================================ */
const SKIP_TAGS = new Set(['STYLE', 'SCRIPT', 'ANNOTATION', 'SEMANTICS']);
const BLOCK_TAGS = new Set([
  'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
  'LI', 'BLOCKQUOTE', 'TR', 'DT', 'DD',
]);

function extractSegments(el) {
  const segments = [];

  function getTextContent(node) {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent;
    if (node.nodeType !== Node.ELEMENT_NODE) return '';
    if (SKIP_TAGS.has(node.tagName)) return '';
    // Skip KaTeX rendered math (aria-hidden spans contain duplicate visual text)
    if (node.classList && node.classList.contains('katex-html')) return '';
    let t = '';
    for (const child of node.childNodes) t += getTextContent(child);
    return t;
  }

  function walk(node) {
    if (node.nodeType === Node.TEXT_NODE) return;
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    if (SKIP_TAGS.has(node.tagName)) return;
    if (node.classList && node.classList.contains('katex-html')) return;

    // Code blocks → each line is its own segment, first line prefixed with "Code:"
    if (node.tagName === 'PRE') {
      const lines = node.textContent.split('\n').map(l => l.trim()).filter(Boolean);
      lines.forEach((line, i) => {
        segments.push(i === 0 ? 'Code: ' + line : line);
      });
      return;
    }

    if (BLOCK_TAGS.has(node.tagName)) {
      const text = getTextContent(node).replace(/\s+/g, ' ').trim();
      if (text) segments.push(text);
      return;
    }

    for (const child of node.childNodes) walk(child);
  }

  walk(el);
  return segments;
}

function extractSegmentsFromMarkdown(md) {
  if (!md) return [];
  const segments = [];

  // Extract fenced code blocks first (3 or 5 backticks), replace with placeholders
  // Keep newlines intact so each line becomes its own segment
  const codeBlocks = [];
  const withPlaceholders = md.replace(/`{3,5}(\w*)\n([\s\S]*?)`{3,5}/g, (_, _lang, code) => {
    const idx = codeBlocks.length;
    codeBlocks.push(code);
    return `\n__CODE_BLOCK_${idx}__\n`;
  });

  const cleaned = withPlaceholders
    .replace(/\$\$[\s\S]*?\$\$/g, '')       // block LaTeX
    .replace(/!\[.*?\]\(.*?\)/g, '')        // images
    .replace(/^\s*\|.*\|\s*$/gm, '')        // table rows (anchored to line)
    .replace(/^---+\s*$/gm, '');            // horizontal rules

  const lines = cleaned.split('\n');

  for (const line of lines) {
    // Check for code block placeholder
    const codeMatch = line.match(/^__CODE_BLOCK_(\d+)__$/);
    if (codeMatch) {
      const code = codeBlocks[parseInt(codeMatch[1], 10)];
      if (code) {
        const codeLines = code.split('\n').map(l => l.trim()).filter(Boolean);
        codeLines.forEach((cl, i) => {
          segments.push(i === 0 ? 'Code: ' + cl : cl);
        });
      }
      continue;
    }

    let text = line
      .replace(/^#{1,6}\s+/, '')            // headings
      .replace(/^\s*\d+\.\s+/, '')          // numbered list
      .replace(/^\s*[-*+]\s+/, '')          // unordered list
      .replace(/^\s*>\s+/, '')              // blockquote
      .replace(/\[([^\]]*)\]\(.*?\)/g, '$1') // links
      .replace(/`([^`]*)`/g, '$1')          // inline code → keep the text
      .replace(/\$[^$\n]*\$/g, '')          // inline LaTeX
      .replace(/(\*{1,3}|_{1,3})(.*?)\1/g, '$2') // bold/italic
      .replace(/\s+/g, ' ')
      .trim();
    if (text) segments.push(text);
  }
  return segments;
}

/* ============================================================
   Floating Play Button — reads project content aloud
   - Each block element is spoken as its own utterance for pacing
   - Reads from text selection if present, otherwise from top
   - Reading speed from localStorage (set in gear dropdown)
============================================================ */
const READING_BORDER = '3px solid var(--brand)';
const READING_PADDING = '0 0 0 12px';

// Collect DOM element references for each speakable segment in document order
function collectBodyNodes() {
  const bodyEl = document.querySelector('.custom-body-markdown');
  if (!bodyEl) return [];
  const nodes = [];

  function walk(node) {
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    if (SKIP_TAGS.has(node.tagName)) return;
    if (node.classList && node.classList.contains('katex-html')) return;

    if (node.tagName === 'PRE') {
      // PRE maps to multiple segments (one per line), but all point to the same PRE node
      const lineCount = node.textContent.split('\n').map(l => l.trim()).filter(Boolean).length;
      for (let i = 0; i < lineCount; i++) nodes.push(node);
      return;
    }

    if (BLOCK_TAGS.has(node.tagName)) {
      const text = node.textContent.replace(/\s+/g, ' ').trim();
      if (text) nodes.push(node);
      return;
    }

    for (const child of node.childNodes) walk(child);
  }

  walk(bodyEl);
  return nodes;
}

const FloatingPlayButton = ({ project }) => {
  const [playState, setPlayState] = useState('stopped'); // stopped | playing | paused
  const synthRef = React.useRef(window.speechSynthesis);
  const segmentsRef = React.useRef([]);
  const nodesRef = React.useRef([]);    // parallel array of DOM nodes
  const headerCountRef = React.useRef(0); // how many segments are header fields (title/subtitle/etc)
  const idxRef = React.useRef(0);
  const prevNodeRef = React.useRef(null); // previously highlighted node
  const playStateRef = React.useRef('stopped');

  // Keep ref in sync with state so keyboard handler sees latest value
  useEffect(() => { playStateRef.current = playState; }, [playState]);

  const getSpeed = () => {
    const saved = localStorage.getItem('i7_read_speed');
    const parsed = saved ? parseFloat(saved) : null;
    return parsed && parsed >= 0.5 && parsed <= 3 ? parsed : 1.0;
  };

  const pauseTimerRef = React.useRef(null);
  const cancelledRef = React.useRef(false);

  const clearPause = () => {
    if (pauseTimerRef.current) {
      clearTimeout(pauseTimerRef.current);
      pauseTimerRef.current = null;
    }
  };

  const clearHighlight = () => {
    if (prevNodeRef.current) {
      prevNodeRef.current.style.borderLeft = '';
      prevNodeRef.current.style.padding = '';
      prevNodeRef.current.style.transition = '';
      prevNodeRef.current = null;
    }
  };

  // Speak a single segment by index, reading speed fresh from localStorage
  // Adds a 250ms pause between segments for natural pacing
  // Highlights the corresponding DOM element with a left border
  const speakAt = useCallback((idx) => {
    const synth = synthRef.current;
    const segs = segmentsRef.current;
    clearPause();
    if (cancelledRef.current || idx < 0 || idx >= segs.length) {
      clearHighlight();
      setPlayState('stopped');
      return;
    }
    idxRef.current = idx;

    // Highlight the corresponding DOM node
    clearHighlight();
    const bodyIdx = idx - headerCountRef.current;
    if (bodyIdx >= 0 && bodyIdx < nodesRef.current.length) {
      const node = nodesRef.current[bodyIdx];
      node.style.transition = 'border-left 150ms ease, padding 150ms ease';
      node.style.borderLeft = READING_BORDER;
      node.style.padding = READING_PADDING;
      prevNodeRef.current = node;
    }

    synth.cancel();
    const utterance = new SpeechSynthesisUtterance(segs[idx]);
    utterance.rate = getSpeed();
    utterance.onend = () => {
      if (!cancelledRef.current) {
        pauseTimerRef.current = setTimeout(() => speakAt(idx + 1), 250);
      }
    };
    utterance.onerror = (e) => {
      if (e.error !== 'interrupted') setPlayState('stopped');
    };
    synth.speak(utterance);
    setPlayState('playing');
  }, []);

  // Cancel speech on mount (handles refresh mid-reading) and on unmount/project change
  useEffect(() => {
    cancelledRef.current = false;
    synthRef.current.cancel();
    return () => {
      cancelledRef.current = true;
      clearPause();
      clearHighlight();
      synthRef.current.cancel();
    };
  }, [project]);

  // Build segments and start playing from the beginning
  const startPlaying = useCallback(() => {
    let segments;

    // Check for text selection — read from selection point onward
    const selection = window.getSelection();
    if (selection && selection.toString().trim().length > 0) {
      const range = selection.getRangeAt(0);
      const bodyEl = document.querySelector('.custom-body-markdown');
      if (bodyEl && bodyEl.contains(range.startContainer)) {
        const fullRange = document.createRange();
        fullRange.setStart(range.startContainer, range.startOffset);
        fullRange.setEndAfter(bodyEl);
        const fragment = fullRange.cloneContents();
        const tempDiv = document.createElement('div');
        tempDiv.appendChild(fragment);
        segments = extractSegments(tempDiv);
      }
    }

    if (!segments || segments.length === 0) {
      segments = [];
      if (project.title) segments.push(project.title);
      if (project.subtitle) segments.push(project.subtitle);
      if (project.technology) segments.push(project.technology);
      if (project.skill) segments.push(project.skill);
      headerCountRef.current = segments.length;
      segments = segments.concat(extractSegmentsFromMarkdown(project.body));
      nodesRef.current = collectBodyNodes();
    } else {
      // Reading from selection — no header segments, no node mapping
      headerCountRef.current = 0;
      nodesRef.current = [];
    }

    if (segments.length === 0) return;

    segmentsRef.current = segments;
    speakAt(0);
  }, [project, speakAt]);

  // Keyboard shortcuts: Space = play/pause/resume, ArrowDown = next, ArrowUp = prev
  useEffect(() => {
    const handleKey = (e) => {
      // Don't capture keys when typing in inputs or editable elements
      const el = document.activeElement;
      const tag = el?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (el?.isContentEditable) return;

      // Check if reading is enabled
      if (localStorage.getItem('i7_read_enabled') === 'false') return;

      const state = playStateRef.current;

      if (e.key === ' ' || e.code === 'Space') {
        e.preventDefault();
        const synth = synthRef.current;
        if (state === 'playing') {
          synth.pause();
          setPlayState('paused');
        } else if (state === 'paused') {
          synth.resume();
          setPlayState('playing');
        } else {
          startPlaying();
        }
      } else if (state !== 'stopped') {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          speakAt(idxRef.current + 1);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          speakAt(Math.max(0, idxRef.current - 1));
        }
      }
    };

    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [speakAt, startPlaying]);

  const handleClick = useCallback(() => {
    const synth = synthRef.current;

    if (playState === 'playing') {
      synth.pause();
      setPlayState('paused');
      return;
    }

    if (playState === 'paused') {
      synth.resume();
      setPlayState('playing');
      return;
    }

    startPlaying();
  }, [playState, startPlaying]);

  const handleStop = useCallback(() => {
    clearPause();
    clearHighlight();
    synthRef.current.cancel();
    setPlayState('stopped');
  }, []);

  // Don't render if browser doesn't support speech synthesis or reading is disabled
  const readEnabled = localStorage.getItem('i7_read_enabled');
  if (!window.speechSynthesis || readEnabled === 'false') return null;

  const isActive = playState !== 'stopped';

  return (
    <div style={{ position: "fixed", bottom: 30, right: 156, zIndex: 1000, display: "flex", gap: 8, alignItems: "center" }}>
      {isActive && (
        <button
          type="button"
          onClick={handleStop}
          aria-label="Stop reading"
          style={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            backgroundColor: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            border: "1px solid var(--border)",
            cursor: "pointer",
          }}
        >
          <StopIcon style={{ color: "#333", fontSize: "1.1rem" }} />
        </button>
      )}
      <button
        type="button"
        onClick={handleClick}
        aria-label={playState === 'playing' ? 'Pause reading' : 'Read project aloud'}
        style={{
          width: 44,
          height: 44,
          borderRadius: "50%",
          backgroundColor: isActive ? "#59c6c1" : "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          border: isActive ? "none" : "1px solid var(--border)",
          cursor: "pointer",
          transition: "background-color 200ms ease",
        }}
      >
        {playState === 'playing'
          ? <PauseIcon style={{ color: "#fff", fontSize: "1.3rem" }} />
          : <PlayArrowIcon style={{ color: isActive ? "#fff" : "#333", fontSize: "1.3rem" }} />
        }
      </button>
    </div>
  );
};

/* ============================================================
   Main
============================================================ */
export default function ProjectDetail() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(null);

  // Detect dark mode from localStorage (same as NavSettingsDropdown)
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('i7-dark-mode');
    if (saved !== null) {
      return JSON.parse(saved);
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Listen for theme changes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.documentElement.classList.contains('dark-theme'));
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  const userPk = useMemo(() => {
    const raw = localStorage.getItem("USER_PK");
    const parsed = raw ? parseInt(raw, 10) : null;
    return Number.isFinite(parsed) ? parsed : null;
  }, []);

  const baseUrl = useMemo(() => Config().baseUrl, []);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    (async () => {
      try {
        setLoading(true);
        setLoadErr(null);

        const res = await axios.get(`${baseUrl}${id}`, {
          signal: controller.signal,
        });
        if (cancelled) return;

        const data = res.data || {};
        setProject(data);

        const ownerPks = Array.isArray(data.owner)
          ? data.owner.map((u) => u?.pk ?? u?.id ?? u)
          : [];

        setCanEdit(userPk !== null && ownerPks.includes(userPk));
      } catch (e) {
        console.error('ProjectDetail Load Error:', e);
        console.error('ProjectDetail Load Error Response:', e.response);
        console.error('ProjectDetail Load Error Status:', e.response?.status);
        console.error('ProjectDetail Load Error Data:', e.response?.data);

        let errorMessage = "Failed to load project";
        if (e.response?.status === 404) {
          errorMessage = "Project not found";
        } else if (e.response?.status === 403) {
          errorMessage = "You don't have permission to view this project";
        } else if (e.response?.status === 500) {
          errorMessage = "Server error occurred while loading project";
        }

        if (!cancelled) setLoadErr(errorMessage);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    window.scrollTo(0, 0);

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [id, userPk, baseUrl]);

  if (loading) return <SweetLoadingSpinner loadingState={true} />;

  if (loadErr || !project) {
    return (
      <div className="container" style={{ padding: "2rem 1rem" }}>
        <h2 style={{ marginBottom: 8 }}>Could not load project</h2>
        <p style={{ color: "#991b1b" }}>{String(loadErr || "Unknown error")}</p>
        <p>
          <Link to="/projects" className="pretty-button">
            Back to Projects
          </Link>
        </p>
      </div>
    );
  }

  const { title, subtitle, technology, skill, body, owner = [] } = project;

  return (
    <div className="container project-detail-custom" style={{ paddingBottom: 96 }}>
      {/* Header */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ padding: "2% 0" }}>
          <h1 style={{ fontWeight: 800, marginBottom: 6 }}>{title}</h1>
          {subtitle ? <h3 style={{ marginTop: 0, fontSize: "0.9em" }}>{subtitle}</h3> : null}
          <p style={{ color: "#59c6c1", marginTop: 12, fontSize: "1em" }}>
            <strong>
              {technology}
              <span style={{ color: "lightgray" }}> | </span>
              {skill}
            </strong>
            <ProjectContributorsList users={owner} />
          </p>
        </div>
      </div>
      <hr />

      {/* Body */}
      <div className="project-detail-body-custom">
        <div style={{ marginTop: 16 }}>
          <ReactMarkdown
            className="custom-body-markdown"
            remarkPlugins={[remarkMath, remarkGfm]}
            rehypePlugins={[[rehypeKatex, { strict: false }]]}
            components={{
              /* Fenced blocks ``` -> PreBlock (copy + Prism) */
              pre: (props) => <PreBlock {...props} isDarkMode={isDarkMode} />,
              /* Inline `code` */
              code: InlineCode,
              /* Tables -> wrap for horizontal scroll */
              table: TableWrap,
            }}
          >
            {body || ""}
          </ReactMarkdown>
        </div>
      </div>

      {/* Floating action buttons */}
      <FloatingPlayButton project={project} />
      <FloatingCopyButton project={project} />
      {canEdit && <FloatingEditButton to={`/projects/${id}/edit`} />}
    </div>
  );
}
